import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "ghost" | "outline" | "danger";
type Size = "sm" | "md" | "lg" | "icon";

const VARIANTS: Record<Variant, string> = {
  primary:   "bg-[#f0ccd1] text-[#4a2f36] hover:bg-[#e8bcc4] active:bg-[#e2adb8] disabled:bg-[#f0ccd1]/40 disabled:text-[#4a2f36]/50",
  secondary: "bg-zinc-800 text-zinc-100 hover:bg-zinc-700 active:bg-zinc-800 disabled:bg-zinc-800/50 disabled:text-zinc-500",
  ghost:     "text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100 active:bg-zinc-800/80",
  outline:   "border border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800/40 hover:border-zinc-600",
  danger:    "bg-rose-600 text-white hover:bg-rose-500 active:bg-rose-700",
};

const SIZES: Record<Size, string> = {
  sm:   "h-8 px-3 text-xs gap-1.5",
  md:   "h-9 px-3.5 text-sm gap-2",
  lg:   "h-10 px-4 text-sm gap-2",
  icon: "h-9 w-9 p-0",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading, leftIcon, rightIcon, children, className, disabled, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex cursor-pointer items-center justify-center rounded-lg font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/50 focus-visible:ring-offset-0",
        "disabled:cursor-not-allowed disabled:opacity-60",
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});
